import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import QuickReferModal from "./QuickReferModal";
import { registerSW } from "../lib/push";
import { brandConfetti, detectCelebration } from "../lib/confetti";
import { toast } from "sonner";
import BadgeCelebrationModal from "./BadgeCelebrationModal";
import {
  LayoutDashboard,
  Briefcase,
  UserPlus,
  ListChecks,
  Trophy,
  Sparkles,
  Bell,
  LogOut,
  KanbanSquare,
  ShieldCheck,
  BarChart3,
  Zap,
  Wallet,
  Instagram,
  Menu,
  X,
  MoreHorizontal,
  Settings,
  Megaphone,
  Flame,
} from "lucide-react";

const NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["employee", "admin"] },
  { to: "/app/jobs", label: "Open Roles", icon: Briefcase, roles: ["employee", "admin"] },
  { to: "/app/refer", label: "Refer a Candidate", icon: UserPlus, roles: ["employee"] },
  { to: "/app/my-referrals", label: "My Referrals", icon: ListChecks, roles: ["employee"] },
  { to: "/app/leaderboard", label: "Leaderboard", icon: Trophy, roles: ["employee", "admin"] },
  { to: "/app/rivalry", label: "Rivalry", icon: Flame, roles: ["employee", "admin"] },
  { to: "/app/spotlight", label: "Spotlight", icon: Instagram, roles: ["employee", "admin"] },
  { to: "/app/shoutouts", label: "Shout-outs", icon: Megaphone, roles: ["employee", "admin"] },
  { to: "/app/badges", label: "Badges", icon: Sparkles, roles: ["employee"] },
  { to: "/app/payout", label: "Request Payout", icon: Wallet, roles: ["employee"] },
  { to: "/app/notifications", label: "Notifications", icon: Bell, roles: ["employee", "admin"] },
  { to: "/app/settings", label: "Settings", icon: Settings, roles: ["employee", "admin"] },
  { to: "/app/pipeline", label: "Pipeline", icon: KanbanSquare, roles: ["admin"] },
  { to: "/app/analytics", label: "Analytics", icon: BarChart3, roles: ["admin"] },
  { to: "/app/admin", label: "Admin", icon: ShieldCheck, roles: ["admin"] },
];

// Bottom tab bar (staff only) — 4 primary + More
const MOBILE_TABS_EMPLOYEE = ["/app/dashboard", "/app/jobs", "/app/refer", "/app/leaderboard"];

function ReferTabButton({ to, Icon, onLongPress }) {
  const navigate = useNavigate();
  const timer = useRef(null);
  const triggered = useRef(false);
  const LONG_MS = 450;

  const start = () => {
    triggered.current = false;
    timer.current = setTimeout(() => {
      triggered.current = true;
      if (navigator.vibrate) {
        try { navigator.vibrate(35); } catch {}
      }
      onLongPress();
    }, LONG_MS);
  };
  const clear = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };
  const onClick = (e) => {
    if (triggered.current) {
      e.preventDefault();
      triggered.current = false;
      return;
    }
    navigate(to);
  };

  return (
    <button
      type="button"
      onClick={onClick}
      onContextMenu={(e) => e.preventDefault()}
      onPointerDown={start}
      onPointerUp={clear}
      onPointerLeave={clear}
      onPointerCancel={clear}
      data-testid="bottom-nav-refer"
      className="flex flex-col items-center justify-center gap-1 text-[10px] uppercase tracking-wider font-bold select-none"
    >
      <div className="w-11 h-11 rounded-xl border-2 border-black flex items-center justify-center bg-brand-orange text-white shadow-brutal -translate-y-2 active:translate-y-0 active:shadow-none transition-transform">
        <Icon className="w-5 h-5" strokeWidth={2.6} />
      </div>
      <span className="text-brand-orange">Refer</span>
    </button>
  );
}

function SidebarContent({ items, user, onNavigate, onLogout, unread }) {
  return (
    <>
      <div className="p-6 border-b-2 border-black">
        <Link to="/app/dashboard" onClick={onNavigate} className="flex items-center gap-3" data-testid="sidebar-logo">
          <div className="w-11 h-11 bg-brand-orange border-2 border-black rounded-xl flex items-center justify-center shadow-brutal">
            <Zap className="w-6 h-6 text-white" strokeWidth={3} />
          </div>
          <div>
            <div className="font-display font-black text-xl leading-none">ReferPro</div>
            <div className="text-[11px] font-semibold tracking-widest text-neutral-500 uppercase">Refer · Earn · Recognise</div>
          </div>
        </Link>
      </div>
      <nav className="flex-1 overflow-y-auto p-4 space-y-1">
        {items.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={onNavigate}
              data-testid={`nav-${n.to.replace("/app/", "")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl font-semibold text-sm border-2 ${
                  isActive
                    ? "bg-brand-yellow border-black shadow-brutal"
                    : "border-transparent hover:border-black hover:bg-neutral-100"
                }`
              }
            >
              <Icon className="w-[18px] h-[18px]" strokeWidth={2.4} />
              <span className="flex-1">{n.label}</span>
              {n.to === "/app/notifications" && unread > 0 && (
                <span className="chip bg-brand-orange text-white text-[10px] py-0 px-2">{unread}</span>
              )}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-4 border-t-2 border-black">
        <div className="flex items-center gap-3 mb-3">
          <img
            src={user.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(user.name)}`}
            alt=""
            className="w-10 h-10 rounded-full border-2 border-black object-cover"
          />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm truncate" data-testid="sidebar-user-name">{user.name}</div>
            <div className="text-[11px] uppercase tracking-widest font-bold text-neutral-500">{user.role}</div>
          </div>
        </div>
        <button onClick={onLogout} className="btn-brutal btn-white w-full text-sm" data-testid="logout-btn">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>
    </>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [quickReferOpen, setQuickReferOpen] = useState(false);
  const [badgeQueue, setBadgeQueue] = useState([]);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Poll unread notifications — toast the newest one when the set changes
  const lastSeenRef = useRef(new Set());
  const firstRunRef = useRef(true);
  useEffect(() => {
    if (!user) return;
    let mounted = true;
    const fetchUnread = () => {
      api.get("/notifications").then((r) => {
        if (!mounted) return;
        const unreadItems = r.data.filter((n) => !n.read);
        setUnread(unreadItems.length);
        const ids = new Set(unreadItems.map((n) => n.id));
        if (!firstRunRef.current) {
          const newOnes = unreadItems.filter((n) => !lastSeenRef.current.has(n.id));
          if (newOnes.length > 0) {
            const first = newOnes[0];
            // Fire confetti for stage-advance + hire notifications (employees only)
            if (user.role === "employee") {
              const intensity = detectCelebration(first);
              if (intensity) brandConfetti({ intensity });
            }
            toast(first.title, {
              description: first.body,
              action: first.link
                ? { label: "Open", onClick: () => navigate(first.link) }
                : undefined,
              duration: 5000,
            });
          }
        }
        lastSeenRef.current = ids;
        firstRunRef.current = false;
      }).catch(() => {});
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 15000);
    return () => { mounted = false; clearInterval(id); };
  }, [user, location.pathname, navigate]);

  // Register service worker (for Web Push) — no prompt yet, just prepare
  useEffect(() => {
    if (!user) return;
    registerSW();
  }, [user]);

  // Detect freshly-unlocked badges and celebrate (employees only)
  const badgeInitRef = useRef(false);
  const knownBadgeIdsRef = useRef(new Set());
  useEffect(() => {
    if (!user || user.role !== "employee") return;
    let mounted = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/badges/me");
        if (!mounted) return;
        const currentIds = new Set(data.map((b) => b.user_badge_id || b.id));
        if (!badgeInitRef.current) {
          knownBadgeIdsRef.current = currentIds;
          badgeInitRef.current = true;
          return;
        }
        const newOnes = data.filter(
          (b) => !knownBadgeIdsRef.current.has(b.user_badge_id || b.id)
        );
        if (newOnes.length > 0) {
          setBadgeQueue((q) => [...q, ...newOnes]);
        }
        knownBadgeIdsRef.current = currentIds;
      } catch {}
    };
    poll();
    const id = setInterval(poll, 20000);
    return () => { mounted = false; clearInterval(id); };
  }, [user]);

  // Hide Emergent badge for any logged-in user
  useEffect(() => {
    const el = document.getElementById("emergent-badge");
    if (!el) return;
    const shouldHide = Boolean(user);
    el.style.display = shouldHide ? "none" : "";
    return () => {
      const b = document.getElementById("emergent-badge");
      if (b) b.style.display = "";
    };
  }, [user]);

  if (!user) return children;

  const items = NAV.filter((n) => n.roles.includes(user.role));

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Bottom tab bar items for mobile (staff only)
  const isEmployee = user.role === "employee";
  const bottomTabs = isEmployee
    ? items.filter((n) => MOBILE_TABS_EMPLOYEE.includes(n.to))
    : [];

  const contentPadBottom = isEmployee ? "pb-24 md:pb-0" : "";

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex flex-1 min-h-0">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col w-72 border-r-2 border-black bg-white sticky top-0 h-screen">
        <SidebarContent items={items} user={user} onLogout={doLogout} unread={unread} />
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-white border-b-2 border-black px-4 h-14 flex items-center justify-between">
        <Link to="/app/dashboard" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-brand-orange border-2 border-black rounded-lg flex items-center justify-center shadow-[2px_2px_0_0_#09090B]">
            <Zap className="w-4 h-4 text-white" strokeWidth={3} />
          </div>
          <span className="font-display font-black text-lg">ReferPro</span>
        </Link>
        <div className="flex items-center gap-2">
          <Link
            to="/app/notifications"
            className="relative w-10 h-10 border-2 border-black rounded-xl flex items-center justify-center bg-white"
            data-testid="mobile-notifications"
          >
            <Bell className="w-4 h-4" />
            {unread > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 bg-brand-orange text-white text-[10px] font-black rounded-full border-2 border-black flex items-center justify-center">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </Link>
          <button
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            className="w-10 h-10 border-2 border-black rounded-xl flex items-center justify-center bg-brand-yellow shadow-[2px_2px_0_0_#09090B]"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-4 h-4" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div
          className="md:hidden fixed inset-0 z-50 flex"
          onClick={() => setDrawerOpen(false)}
          data-testid="mobile-drawer"
        >
          <div
            className="ml-auto w-[85%] max-w-sm bg-white border-l-2 border-black h-full flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-end p-3 border-b-2 border-black">
              <button
                onClick={() => setDrawerOpen(false)}
                className="w-9 h-9 border-2 border-black rounded-xl flex items-center justify-center"
                data-testid="mobile-drawer-close"
                aria-label="Close menu"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <SidebarContent
              items={items}
              user={user}
              onNavigate={() => setDrawerOpen(false)}
              onLogout={doLogout}
              unread={unread}
            />
          </div>
        </div>
      )}

      <main className={`flex-1 min-w-0 pt-14 md:pt-0 ${contentPadBottom}`}>
        <div className="max-w-[1400px] mx-auto p-4 sm:p-6 md:p-8">{children}</div>
      </main>

      {/* Mobile bottom tab bar (staff only) */}
      {isEmployee && bottomTabs.length > 0 && (
        <nav
          className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t-2 border-black h-[72px] pb-[env(safe-area-inset-bottom)]"
          data-testid="mobile-bottom-nav"
        >
          <div className="grid grid-cols-5 h-[72px]">
            {bottomTabs.map((t) => {
              const Icon = t.icon;
              const active = location.pathname === t.to;
              const isRefer = t.to === "/app/refer";
              if (isRefer) {
                return (
                  <ReferTabButton
                    key={t.to}
                    to={t.to}
                    Icon={Icon}
                    onLongPress={() => setQuickReferOpen(true)}
                  />
                );
              }
              return (
                <NavLink
                  key={t.to}
                  to={t.to}
                  data-testid={`bottom-nav-${t.to.replace("/app/", "")}`}
                  className="flex flex-col items-center justify-center gap-1 text-[10px] uppercase tracking-wider font-bold"
                >
                  <div
                    className={`w-11 h-11 rounded-xl border-2 border-black flex items-center justify-center transition-shadow ${
                      active ? "bg-brand-yellow shadow-brutal" : "bg-white"
                    }`}
                  >
                    <Icon className="w-5 h-5" strokeWidth={2.6} />
                  </div>
                  <span className={active ? "text-black" : "text-neutral-600"}>
                    {t.label.split(" ")[0]}
                  </span>
                </NavLink>
              );
            })}
            <button
              onClick={() => setDrawerOpen(true)}
              className="flex flex-col items-center justify-center gap-1 text-[10px] uppercase tracking-wider font-bold"
              data-testid="bottom-nav-more"
            >
              <div className="w-11 h-11 rounded-xl border-2 border-black flex items-center justify-center bg-white">
                <MoreHorizontal className="w-5 h-5" strokeWidth={2.6} />
              </div>
              <span className="text-neutral-600">More</span>
            </button>
          </div>
        </nav>
      )}

      <QuickReferModal open={quickReferOpen} onClose={() => setQuickReferOpen(false)} />

      <BadgeCelebrationModal
        badge={badgeQueue[0] || null}
        onClose={() => setBadgeQueue((q) => q.slice(1))}
      />
      </div>
    </div>
  );
}
